/**
 * Student Documents Page
 * Students can VIEW their uploaded documents (READ-ONLY)
 * Students CANNOT delete documents - only Super Admin can
 */
import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useStudentAuth } from '@/context/StudentAuthContext';
import { useAssetContext } from '@/context/AssetContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  FileText,
  Upload,
  CheckCircle,
  Clock,
  Shield,
  ArrowLeft,
  ExternalLink,
  AlertCircle,
  Loader2,
  Eye
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = window.location.origin;

const DOCUMENT_TYPES = {
  aadhaar: {
    name: 'Aadhaar Card',
    description: 'Identity verification',
    required: true,
    bonus: '+3%'
  },
  '10th_marksheet': {
    name: '10th Marksheet',
    description: 'Academic record',
    required: false,
    bonus: '+3%'
  },
  '12th_marksheet': {
    name: '12th Marksheet',
    description: 'Academic record',
    required: false,
    bonus: '+3%'
  },
  degree_certificate: {
    name: 'Degree Certificate',
    description: 'Academic qualification',
    required: false,
    bonus: '+3%'
  },
  transcripts: {
    name: 'Academic Transcripts',
    description: 'Semester-wise marks',
    required: false,
    bonus: '+2%'
  },
  it_returns: {
    name: 'IT Returns',
    description: 'Financial verification',
    required: false,
    bonus: '+5%'
  },
  passport: {
    name: 'Passport',
    description: 'Travel document',
    required: false,
    bonus: '+2%'
  },
  english_scorecard: {
    name: 'English Scorecard',
    description: 'IELTS/PTE/TOEFL',
    required: false,
    bonus: '+5%'
  }
};

const StudentDocumentsPage = () => {
  const { user, token, loading: authLoading } = useStudentAuth();
  const { getLogo } = useAssetContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [documents, setDocuments] = useState({});
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null);
  
  // Get return URL from state (default to dashboard)
  const returnTo = location.state?.returnTo || '/uac-desk/dashboard';

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/uac-desk/login');
      return;
    }
    loadDocuments();
  }, [user, authLoading]);

  const loadDocuments = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/counselor/profile/${user.user_id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const profile = await res.json();
        setDocuments(profile.documents || {});
      }
    } catch (error) {
      console.error('Error loading documents:', error);
      toast.error('Failed to load documents');
    }
    setLoading(false);
  };

  const handleFileUpload = async (docType, file) => {
    if (!file) return;
    
    // Validate file
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      toast.error('File size must be less than 5MB');
      return;
    }
    
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only PDF, JPG, and PNG files are allowed');
      return;
    }
    
    setUploading(docType);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('doc_type', docType);
      
      const res = await fetch(`${API_URL}/api/counselor/upload-document/${user.user_id}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`${DOCUMENT_TYPES[docType]?.name || docType} uploaded successfully!`);
        
        // Update local state
        setDocuments(prev => ({
          ...prev,
          [docType]: {
            uploaded: true,
            filename: file.name,
            uploaded_at: new Date().toISOString(),
            verified_via: 'manual',
            file_size: file.size
          }
        }));
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to upload document');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload document');
    }
    
    setUploading(null);
  };

  const [digiLockerLoading, setDigiLockerLoading] = useState(false);
  const [digiLockerConnected, setDigiLockerConnected] = useState(false);
  
  const connectDigiLocker = async () => {
    if (!user?.user_id) {
      toast.error('Please login to connect DigiLocker');
      return;
    }
    
    setDigiLockerLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/digilocker/initiate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          student_id: user.user_id,
          signup_flow: true
        })
      });
      
      const data = await response.json();
      
      if (data.success && data.authorization_url) {
        // Store client ID for callback
        sessionStorage.setItem('digilocker_client_id', data.client_id);
        
        // Redirect to DigiLocker
        toast.info('Redirecting to DigiLocker...');
        window.location.href = data.authorization_url;
      } else {
        toast.error(data.message || data.detail || 'Failed to initiate DigiLocker session');
      }
    } catch (error) {
      console.error('DigiLocker error:', error);
      toast.error('Failed to connect to DigiLocker. Please try again.');
    } finally {
      setDigiLockerLoading(false);
    }
  };

  // Note: DigiLocker callback is handled by the dedicated /uac-desk/documents/digilocker-callback route
  // (see DigiLockerCallback.jsx)

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-ux-blue" />
      </div>
    );
  }

  const uploadedCount = Object.values(documents).filter(d => d?.uploaded).length;

  return (
    <div className="min-h-screen w-full max-w-[100vw] overflow-x-hidden bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-3 sm:px-4 lg:px-8 py-4 w-full">
        <div className="w-full max-w-4xl mx-auto flex items-center gap-3 sm:gap-4">
          <Link to="/uac-desk/dashboard">
            <img 
              src={getLogo()} 
              alt="UniXperts" 
              className="h-7 w-auto object-contain hidden sm:block"
            />
          </Link>
          <div className="h-6 w-px bg-slate-200 hidden sm:block" />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(returnTo, { state: { autoResume: true, fromDocuments: true } })}
            className="text-slate-500 hover:text-slate-800"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            <span className="hidden sm:inline">Back</span>
          </Button>
          <div>
            <h1 className="text-xl font-bold text-slate-900 font-display">My Documents</h1>
            <p className="text-sm text-slate-500">{uploadedCount} of {Object.keys(DOCUMENT_TYPES).length} documents uploaded</p>
          </div>
        </div>
      </header>

      <main className="w-full max-w-4xl mx-auto p-3 sm:p-4 lg:p-8">
        {/* DigiLocker Banner - Active Integration */}
        <div className={`bg-gradient-to-r ${digiLockerConnected ? 'from-green-50 to-emerald-50 border-green-200' : 'from-blue-50 to-indigo-50 border-blue-200'} border rounded-2xl p-6 mb-6 relative overflow-hidden`}>
          {digiLockerConnected && (
            <div className="absolute top-3 right-3 bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
              <CheckCircle className="w-3 h-3" />
              Connected
            </div>
          )}
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 rounded-xl ${digiLockerConnected ? 'bg-green-100' : 'bg-blue-100'} flex items-center justify-center flex-shrink-0`}>
              <Shield className={`w-6 h-6 ${digiLockerConnected ? 'text-green-600' : 'text-blue-600'}`} />
            </div>
            <div className="flex-1">
              <h3 className={`font-semibold ${digiLockerConnected ? 'text-green-900' : 'text-blue-900'}`}>
                DigiLocker Integration
              </h3>
              <p className={`text-sm ${digiLockerConnected ? 'text-green-700' : 'text-blue-700'} mt-1`}>
                {digiLockerConnected 
                  ? 'Your documents have been verified via DigiLocker. You get +3% profile boost per verified document!'
                  : 'Auto-fetch verified government documents (Aadhaar, PAN) and get +3% profile boost per document!'
                }
              </p>
              {!digiLockerConnected && (
                <p className="text-xs text-blue-500 mt-2">
                  Documents fetched: Aadhaar Card, PAN Card • Secure & Government Verified
                </p>
              )}
              <Button
                onClick={connectDigiLocker}
                disabled={digiLockerLoading || digiLockerConnected}
                className={`mt-3 ${digiLockerConnected ? 'bg-green-500' : 'bg-blue-600 hover:bg-blue-700'} text-white`}
                size="sm"
                data-testid="connect-digilocker-btn"
              >
                {digiLockerLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : digiLockerConnected ? (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Documents Verified
                  </>
                ) : (
                  <>
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Connect DigiLocker
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Important Notice */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800">Important Notice</p>
            <p className="text-xs text-amber-700 mt-1">
              Once uploaded, documents cannot be deleted by students for security reasons. 
              Contact support if you need to replace a document.
            </p>
          </div>
        </div>

        {/* Document Grid */}
        <div className="grid gap-4">
          {Object.entries(DOCUMENT_TYPES).map(([docType, docInfo]) => {
            const docData = documents[docType];
            const isUploaded = docData?.uploaded;
            const isUploading = uploading === docType;
            
            return (
              <div
                key={docType}
                className={`bg-white rounded-xl border ${isUploaded ? 'border-emerald-200' : 'border-slate-200'} p-4 lg:p-5`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4 flex-1">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                      isUploaded ? 'bg-emerald-100' : 'bg-slate-100'
                    }`}>
                      {isUploaded ? (
                        <CheckCircle className="w-6 h-6 text-emerald-600" />
                      ) : (
                        <FileText className="w-6 h-6 text-slate-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-slate-900">{docInfo.name}</h3>
                        {docInfo.required && (
                          <Badge variant="outline" className="text-rose-600 border-rose-200 text-xs">
                            Required
                          </Badge>
                        )}
                        <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                          {docInfo.bonus} boost
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-500 mt-0.5">{docInfo.description}</p>
                      
                      {isUploaded && (
                        <div className="mt-2 flex items-center gap-4 text-xs text-slate-500">
                          <span className="flex items-center gap-1">
                            <FileText className="w-3 h-3" />
                            {docData.filename || 'Document'}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {new Date(docData.uploaded_at).toLocaleDateString()}
                          </span>
                          {docData.verified_via === 'digilocker' && (
                            <span className="flex items-center gap-1 text-blue-600">
                              <Shield className="w-3 h-3" />
                              DigiLocker Verified
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex-shrink-0">
                    {isUploaded ? (
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-slate-600"
                        onClick={() => {
                          // View document (if file_path exists)
                          if (docData.file_path) {
                            window.open(`${API_URL}${docData.file_path}`, '_blank');
                          } else {
                            toast.info('Document preview not available');
                          }
                        }}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </Button>
                    ) : (
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          className="hidden"
                          onChange={(e) => handleFileUpload(docType, e.target.files[0])}
                          disabled={isUploading}
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-ux-blue border-ux-blue hover:bg-ux-blue/5"
                          disabled={isUploading}
                          asChild
                        >
                          <span>
                            {isUploading ? (
                              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                            ) : (
                              <Upload className="w-4 h-4 mr-1" />
                            )}
                            Upload
                          </span>
                        </Button>
                      </label>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Help Text */}
        <div className="mt-6 text-center text-sm text-slate-500">
          <p>Accepted formats: PDF, JPG, PNG (max 5MB)</p>
          <p className="mt-1">
            Need help? <a href="https://wa.me/919876543210" target="_blank" rel="noopener noreferrer" className="text-ux-blue hover:underline">Contact support</a>
          </p>
        </div>
      </main>
    </div>
  );
};

export default StudentDocumentsPage;
